// Hardcoded demo data for the free-tier supplier journey mockup (/supplier).
// Everything on these pages reads and mutates this shape via MockDataContext;
// no API calls are made anywhere in the FreeTierMockup module.
//
// Field sets deliberately mirror the real regulatory forms:
// - company        → Operator/OperatorInfoForm.jsx
// - installations  → OperatorInstallations/CreateEditModal/CreateEditModal.jsx
// - emissionFactors→ Installations/components/emissionsFormSchema.js (Tab 1)
//                    + one production method (Tab 2)
//
// i18n: display fields below are getters (not plain strings), same pattern as
// Common/config/lockedFeatures.js — this file is plain data, not a component,
// so there is no useTranslation() to call; every consumer reads a
// live-translated value through i18n.t() instead. Fake company/installation
// names and emails stay literal (not copy to translate) — see D-declarant-i18n
// AUDIT-LEDGER "proper nouns" rule.
import i18n from 'i18n';
import { roundNonZero } from 'helpers/numberFormat';

// v4: adds dismissedUpsells + gap-engine derived state (older state is discarded)
export const STORAGE_KEY = 'cg-lite-mockup-v4';

// "Today" for the demo — drives the stale-reporting-period gap.
export const CURRENT_REPORTING_YEAR = '2026';

export const COUNTRY_OPTIONS = [
  {
    value: 'TR',
    get label() {
      return i18n.t('freeSupplier:mockData.country.TR', 'Türkiye');
    },
  },
  {
    value: 'IN',
    get label() {
      return i18n.t('freeSupplier:mockData.country.IN', 'India');
    },
  },
  {
    value: 'CN',
    get label() {
      return i18n.t('freeSupplier:mockData.country.CN', 'China');
    },
  },
  {
    value: 'UA',
    get label() {
      return i18n.t('freeSupplier:mockData.country.UA', 'Ukraine');
    },
  },
  {
    value: 'EG',
    get label() {
      return i18n.t('freeSupplier:mockData.country.EG', 'Egypt');
    },
  },
  {
    value: 'BR',
    get label() {
      return i18n.t('freeSupplier:mockData.country.BR', 'Brazil');
    },
  },
];

// Method ids/names and qualifying parameters taken from
// Installations/data/ProdMethods.json ("Crude steel"). The real form scopes
// both lists to the selected CN good's category; the mockup ships the steel
// set since every demo good is steel.
export const MOCK_PRODUCTION_METHODS = [
  {
    id: 'P35',
    get name() {
      return i18n.t('freeSupplier:mockData.productionMethod.P35', 'Basic oxygen steelmaking');
    },
  },
  {
    id: 'P36',
    get name() {
      return i18n.t(
        'freeSupplier:mockData.productionMethod.P36',
        'Basic oxygen steelmaking (incl. blast furnace)',
      );
    },
  },
  {
    id: 'P37',
    get name() {
      return i18n.t(
        'freeSupplier:mockData.productionMethod.P37',
        'Basic oxygen steelmaking (incl. melting reduction)',
      );
    },
  },
  {
    id: 'P38',
    get name() {
      return i18n.t('freeSupplier:mockData.productionMethod.P38', 'Electric arc furnace');
    },
  },
  {
    id: 'P39',
    get name() {
      return i18n.t(
        'freeSupplier:mockData.productionMethod.P39',
        'Electric arc furnace (alloy steels)',
      );
    },
  },
  {
    id: 'P40',
    get name() {
      return i18n.t(
        'freeSupplier:mockData.productionMethod.P40',
        'Electric arc furnace (carbon steel, from direct reduced iron)',
      );
    },
  },
];

// Looks up a translated production-method/qualifying-parameter name by id, so
// every demo record below that reuses the same method/parameter (e.g. "Basic
// oxygen steelmaking" appears in three separate factors) shares the one i18n
// key above instead of drifting.
const productionMethodName = (id) => MOCK_PRODUCTION_METHODS.find((m) => m.id === id)?.name;
const qualifyingParameterName = (id) => QUALIFYING_PARAMETERS.find((p) => p.id === id)?.name;

export const QUALIFYING_PARAMETERS = [
  {
    id: 'QPD11',
    get name() {
      return i18n.t('freeSupplier:mockData.qualifyingParameter.QPD11', 'Precursor reducing Agent');
    },
    valueType: 'STRING',
    // Enum choices aren't rendered by any live surface today (the CN-catalog
    // qualifying-parameter picker owns its own copy) — left as literal data
    // rather than adding an unused translation surface.
    enumValues: [
      'Coke',
      'Coal',
      'Fuel oil',
      'Natural gas',
      'Hydrogen',
      'Charcoal or biomass',
      'Other',
      'Not applicable',
    ],
  },
  {
    id: 'QPD13',
    get name() {
      return i18n.t('freeSupplier:mockData.qualifyingParameter.QPD13', 'Manganese content');
    },
    valueType: 'PERCENTAGE',
  },
  {
    id: 'QPD14',
    get name() {
      return i18n.t('freeSupplier:mockData.qualifyingParameter.QPD14', 'Chromium content');
    },
    valueType: 'PERCENTAGE',
  },
  {
    id: 'QPD15',
    get name() {
      return i18n.t('freeSupplier:mockData.qualifyingParameter.QPD15', 'Nickel content');
    },
    valueType: 'PERCENTAGE',
  },
  {
    id: 'QPD16',
    get name() {
      return i18n.t(
        'freeSupplier:mockData.qualifyingParameter.QPD16',
        'Content of other alloy elements',
      );
    },
    valueType: 'PERCENTAGE',
  },
  {
    id: 'QPD17',
    get name() {
      return i18n.t('freeSupplier:mockData.qualifyingParameter.QPD17', 'Steel scrap usage');
    },
    valueType: 'NUMBER',
  },
  {
    id: 'QPD18',
    get name() {
      return i18n.t('freeSupplier:mockData.qualifyingParameter.QPD18', 'Steel pre-consumer scrap');
    },
    valueType: 'PERCENTAGE',
  },
];

export const factorMethodsLabel = (factor) => {
  const methods = factor.productionMethods || [];
  if (methods.length === 0) return '—';
  return methods.length === 1 ? methods[0].name : `${methods[0].name} +${methods.length - 1}`;
};

// Mirrors electricitySourceOptions in Installations/components/EmissionsDataTab.jsx
export const ELECTRICITY_SOURCE_OPTIONS = [
  {
    value: 'SOE01',
    get label() {
      return i18n.t(
        'freeSupplier:mockData.electricitySource.SOE01',
        'Direct technical link to electricity generator (SOE01)',
      );
    },
  },
  {
    value: 'SOE02',
    get label() {
      return i18n.t(
        'freeSupplier:mockData.electricitySource.SOE02',
        '(Bilateral) power purchase agreement (SOE02)',
      );
    },
  },
  {
    value: 'SOE03',
    get label() {
      return i18n.t(
        'freeSupplier:mockData.electricitySource.SOE03',
        'Received from the grid (SOE03)',
      );
    },
  },
];

export const REPORTING_YEAR_OPTIONS = ['2026', '2025', '2024', '2023'];

// Period labels used by data requests (free-text periods, not factor dates).
export const REPORTING_PERIOD_OPTIONS = ['2025', '2026 H1', 'Q1 2026', 'Q2 2026'];

export const countryLabel = (value) =>
  COUNTRY_OPTIONS.find((o) => o.value === value)?.label || value;

export const totalSee = (factor) =>
  roundNonZero((factor.seeDirect || 0) + (factor.seeIndirect || 0), 2);

// Mirrors the real portal's chargeable basis: Iron & Steel (CN 72/73) and
// Aluminium (CN 76) are charged on Direct SEE, everything else on Total SEE.
export const isDirectBasis = (cnCode) => /^7[236]/.test(String(cnCode || ''));

// (basis − SEFA) × certificate price; free tier has no SEFA, so SEFA = 0.
export const cbamCostPerTonne = (factor, certificatePrice) => {
  const base = isDirectBasis(factor.cnCode) ? factor.seeDirect || 0 : totalSee(factor);
  return Math.max(0, base) * (Number(certificatePrice) || 0);
};

export const DEFAULT_CERTIFICATE_PRICE = 72;

// Plausible EU default benchmarks (tCO2e/t) per demo CN good — used by the
// SEFA savings estimator: SEFA ≈ benchmark × CBAM factor (0.975 for 2026).
export const EU_DEFAULT_BENCHMARKS = {
  72081000: 1.28,
  72091500: 1.42,
  72071111: 1.18,
  27040011: 0.9,
  25231000: 0.52,
  76011000: 1.51,
};

export const CBAM_FACTOR = 0.975;

// Mock SEFA a Premium user would get for a factor's good (null if no benchmark).
export const estimatedSefa = (factor) => {
  const benchmark = EU_DEFAULT_BENCHMARKS[factor.cnCode];
  if (!benchmark) return null;
  return roundNonZero(benchmark * CBAM_FACTOR, 4);
};

// "01/01/2025 – 31/12/2025" → "2025"; anything else shows the range.
export const factorPeriodLabel = (factor) => {
  const { periodFrom, periodTo } = factor;
  if (!periodFrom || !periodTo) return '—';
  const fy = periodFrom.slice(0, 4);
  const ty = periodTo.slice(0, 4);
  if (fy === ty && periodFrom.endsWith('-01-01') && periodTo.endsWith('-12-31')) return fy;
  const fmt = (d) => d.split('-').reverse().join('/');
  return `${fmt(periodFrom)} – ${fmt(periodTo)}`;
};

export const initialMockData = {
  // Field set mirrors OperatorInfoForm (General / Address / Contact Information).
  company: {
    name: 'Nordfer Steel Works', // i18n-ok: demo proper noun (fake company/site name), not UI copy
    corporateRegistrationNumber: '',
    country: 'TR',
    city: 'Izmir',
    contactName: 'Ayşe Demir',
    contactEmail: 'ayse@nordfer.com',
    phoneNumber: '',
    sector: 'Iron & Steel', // display-only on the portal, not part of the form
    saved: false, // flips true when the form is saved → completes checklist step
  },
  // Field set mirrors CreateEditOperatorInstallation (basic info + optional
  // address details + authorized representative).
  installations: [
    {
      id: 'inst-1',
      name: 'Izmir Plant', // i18n-ok: demo proper noun (fake company/site name), not UI copy
      internalId: 'INST-001',
      cbamRegistryId: '',
      country: 'TR',
      city: 'Izmir',
      street: 'Ataturk OSB, 10023 Sk. No:5',
      postcode: '35620',
      POBox: 'PK 145',
      unlocode: 'TRIZM',
      producerName: '',
      latitude: '38.4237',
      longitude: '27.1428',
    },
  ],
  // Field set mirrors emissionsFormSchema Tab 1 + one production method.
  emissionFactors: [
    {
      id: 'ef-1',
      installationId: 'inst-1',
      cnCode: '72081000',
      productId: 'NF-HR-01',
      productionMethods: [
        {
          methodCode: 'P35',
          get name() {
            return productionMethodName('P35');
          },
          parameters: [
            {
              id: 'QPD11',
              get name() {
                return qualifyingParameterName('QPD11');
              },
              valueType: 'STRING',
              value: 'Coke',
              description: '',
            },
            {
              id: 'QPD13',
              get name() {
                return qualifyingParameterName('QPD13');
              },
              valueType: 'PERCENTAGE',
              value: '0.8',
              description: '',
            },
          ],
        },
      ],
      periodFrom: '2025-01-01',
      periodTo: '2025-12-31',
      seeDirect: 1.92,
      seeIndirect: 0.22,
      electricityConsumed: 0.4,
      electricityFactor: 0.55,
      electricityFactorSource: 'National grid factor (TEİAŞ)',
      electricitySource: 'SOE03',
      shareDefaultEmissions: 0,
      otherSourceIndication: '',
      source: 'Manual entry',
      verified: false,
    },
    {
      id: 'ef-2',
      installationId: 'inst-1',
      cnCode: '72091500',
      productId: 'NF-CR-02',
      productionMethods: [
        {
          methodCode: 'P35',
          get name() {
            return productionMethodName('P35');
          },
          parameters: [
            {
              id: 'QPD17',
              get name() {
                return qualifyingParameterName('QPD17');
              },
              valueType: 'NUMBER',
              value: '0.12',
              get description() {
                return i18n.t(
                  'freeSupplier:mockData.demoNote.postConsumerScrapIncluded',
                  'Post-consumer scrap included',
                );
              },
            },
          ],
        },
      ],
      periodFrom: '2025-01-01',
      periodTo: '2025-12-31',
      seeDirect: 2.14,
      seeIndirect: 0.31,
      electricityConsumed: 0.56,
      electricityFactor: 0.55,
      electricityFactorSource: 'National grid factor (TEİAŞ)',
      electricitySource: 'SOE03',
      shareDefaultEmissions: 0,
      otherSourceIndication: '',
      source: 'Manual entry',
      verified: false,
    },
  ],
  // Template upload result; transient parse status lives on the page,
  // only the completed result is persisted here.
  upload: { result: null },
  // NOTE: no `share` block here — the Supplier Page/widget publication state
  // (S9) is real API data via useSupplierPublication.js, never seeded from
  // this mock (unlike `widget` below, which stays genuinely client-only).
  requests: [
    {
      id: 'req-1',
      direction: 'incoming',
      company: 'Hamburg Imports GmbH',
      type: 'Declarant',
      goods: ['72081000'],
      period: 'Q1 2026',
      message: 'Requesting SEE data for our quarterly CBAM report.',
      status: 'pending',
      date: '2026-07-06',
    },
    {
      id: 'req-2',
      direction: 'incoming',
      company: 'Rotterdam Trading BV',
      type: 'Declarant',
      goods: ['72091500'],
      period: '2025',
      message: '',
      status: 'pending',
      date: '2026-07-08',
    },
    {
      id: 'req-3',
      direction: 'outgoing',
      company: 'Anatolia Coke & Energy',
      type: 'Operator',
      goods: ['27040011'],
      period: '2025',
      message: 'Need precursor emissions for our hot-rolled goods.',
      status: 'accepted',
      date: '2026-06-28',
    },
  ],
  // Client-only cosmetic widget prefs (D-S9-3: decoupled from the CBAM-portal
  // slug — the widget URL now derives from the real publication slug). No
  // `showContact`/`hidePoweredBy` — the real widget subset (D-S9-8) has no
  // "Request data" CTA, and attribution is mandatory, never hideable.
  widget: {
    theme: '#00B894',
    layout: 'card',
    showFactors: true,
    copiedOnce: false,
  },
  // Keys of upsell slots the user closed — dismissals survive reloads.
  dismissedUpsells: [],
};

// Emission factors "extracted" from the uploaded communication template.
export const parsedTemplateFactors = [
  {
    cnCode: '72071111',
    productId: 'NF-SF-03',
    productionMethods: [
      {
        methodCode: 'P36',
        get name() {
          return productionMethodName('P36');
        },
        parameters: [
          {
            id: 'QPD11',
            get name() {
              return qualifyingParameterName('QPD11');
            },
            valueType: 'STRING',
            value: 'Coke',
            description: '',
          },
        ],
      },
    ],
    periodFrom: '2025-01-01',
    periodTo: '2025-12-31',
    seeDirect: 1.78,
    seeIndirect: 0.18,
    electricityConsumed: 0.33,
    electricityFactor: 0.55,
    electricityFactorSource: 'Communication template',
    electricitySource: 'SOE03',
    shareDefaultEmissions: 0,
    otherSourceIndication: '',
    source: 'Communication template',
    verified: false,
  },
];

// Installation created if the parsed template's installation doesn't exist yet.
export const parsedTemplateInstallation = {
  name: 'Izmir Plant', // i18n-ok: demo proper noun (fake company/site name), not UI copy
  internalId: 'INST-001',
  cbamRegistryId: '',
  country: 'TR',
  city: 'Izmir',
  street: 'Ataturk OSB, 10023 Sk. No:5',
  postcode: '35620',
  POBox: 'PK 145',
  unlocode: 'TRIZM',
  producerName: '',
  latitude: '38.4237',
  longitude: '27.1428',
};

// Summary card shown after the simulated template parse completes.
export const parsedTemplateResult = {
  templateType: 'EU Communication Template',
  installationName: 'Izmir Plant',
  reportingPeriod: '2025',
  goodsCount: 3,
  productionStepsCount: 5,
  factorsExtracted: 1,
};
